import { Fragment } from 'react';
import { ChevronRight, X } from 'lucide-react';
import { PillButton } from '@rocco/components/pill-button';
import { getCategoryTrail } from '@rocco/ui/filter-sort/utils/category-tree';
import type { CategoryFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/category-filter-state';

export interface FiltersModalCategoryTreeProps {
    /** The staged category state, read from the sheet's local filter states. */
    categoryState: CategoryFilterState;
    /** Label for the root crumb, e.g. "All Products". */
    rootLabel: string;
    /** Stage the category with this canonical URL as the pending selection. */
    onStageCategory: (url: string) => void;
    /**
     * Render the current node's children as pills. Sub-views show the trail
     * only — enough context and drill-up control without the pill noise;
     * choosing a deeper category stays a root-view activity.
     */
    showChildren?: boolean;
}

/**
 * Inline staged category tree for the mobile refine sheet: a breadcrumb
 * trail back to the vertical root plus the current node's children as
 * pills. Taps stage a pending category; nothing navigates until the sheet's
 * commit CTA. The ✕ on the current crumb removes just that leaf — staging
 * its parent, one level up — while deeper resets go through the ancestor
 * crumbs.
 */
export const FiltersModalCategoryTree = (
    props: FiltersModalCategoryTreeProps,
) => {
    const {
        categoryState,
        rootLabel,
        onStageCategory,
        showChildren = true,
    } = props;
    const { category, selectedCategory } = categoryState;

    const current = selectedCategory ?? category;
    const trail = getCategoryTrail(category, current.url);

    // The staged node is not in the tree (stale data) — hide gracefully.
    if (trail.length === 0) return null;

    const isAtRoot = trail.length === 1;

    // Trail-only mode has nothing to show at the root.
    if (!showChildren && isAtRoot) return null;
    const descendants = trail.slice(1);
    const parentOfCurrent = trail[trail.length - 2] ?? category;

    return (
        <section aria-label="Category" className="flex flex-col">
            <div className="flex min-h-11 items-center text-base font-medium">
                Category
            </div>

            {!isAtRoot && (
                <nav
                    aria-label="Category trail"
                    className="flex flex-wrap items-center gap-x-1 text-sm"
                >
                    <button
                        type="button"
                        className="min-h-8 cursor-pointer text-gray-500 underline-offset-2 hover:underline"
                        onClick={() => onStageCategory(category.url)}
                    >
                        {rootLabel}
                    </button>
                    {descendants.map((node, index) => {
                        const isCurrent = index === descendants.length - 1;

                        return (
                            <Fragment key={node.url}>
                                <ChevronRight
                                    aria-hidden
                                    className="size-3.5 shrink-0 text-gray-400"
                                />
                                {isCurrent ? (
                                    <span
                                        aria-current="location"
                                        className="flex min-h-8 items-center gap-1 font-medium"
                                    >
                                        {node.title}
                                        <button
                                            type="button"
                                            aria-label={`Remove category ${node.title}`}
                                            className="-m-1 cursor-pointer p-2 text-gray-500 hover:text-gray-900"
                                            onClick={() =>
                                                onStageCategory(
                                                    parentOfCurrent.url,
                                                )
                                            }
                                        >
                                            <X className="size-3.5" />
                                        </button>
                                    </span>
                                ) : (
                                    <button
                                        type="button"
                                        className="min-h-8 cursor-pointer text-gray-500 underline-offset-2 hover:underline"
                                        onClick={() =>
                                            onStageCategory(node.url)
                                        }
                                    >
                                        {node.title}
                                    </button>
                                )}
                            </Fragment>
                        );
                    })}
                </nav>
            )}

            {showChildren && current.children.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1 pb-2">
                    {current.children.map((child) => (
                        <PillButton
                            key={child.url}
                            size="sm"
                            disabled={child.disabled}
                            onClick={() => onStageCategory(child.url)}
                        >
                            {child.title}
                        </PillButton>
                    ))}
                </div>
            )}
        </section>
    );
};
